import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

export default function Team() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/team")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  useEffect(() => { load(); }, []);

  if (!user) return <div className="tb-loading">{t("common.loading")}</div>;
  const isAccelerator = user.role === "accelerator";
  const canManageTeam = (user.role === "entrepreneur" || isAccelerator) && !user.team_owner_id;
  if (!canManageTeam && user.role !== "entrepreneur" && !isAccelerator) {
    return (
      <div className="tb-page">
        <h1 className="tb-h1">{t("team.title")}</h1>
        <p className="tb-muted">{t("team.foundersOnly")}</p>
      </div>
    );
  }
  if ((user.role === "entrepreneur" || isAccelerator) && user.team_owner_id) {
    return (
      <div className="tb-page">
        <h1 className="tb-h1">{isAccelerator ? t("team.acceleratorTitle") : t("team.title")}</h1>
        <p className="tb-muted">{t("team.foundersOnly")}</p>
      </div>
    );
  }
  if (!data) return <div className="tb-loading">{t("common.loading")}</div>;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setOk("");
    try {
      const res = await api.post("/team/invites", form);
      if (res.data.status === "already_member") setOk(t("team.alreadyMember"));
      else if (res.data.email_sent === false) setErr(t("team.emailFailed"));
      setForm({ email: "", name: "" });
      setAdding(false);
      load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const cancelInvite = async (id) => {
    if (!window.confirm(t("team.confirmCancel"))) return;
    await api.delete(`/team/invites/${id}`);
    load();
  };
  const resendInvite = async (id) => {
    setBusy(true); setErr(""); setOk("");
    try {
      const res = await api.post(`/team/invites/${id}/resend`);
      if (res.data.email_sent === false) setErr(t("team.emailFailed"));
      else setOk(t("team.resent"));
      load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  const removeMember = async (id) => {
    if (!window.confirm(t("team.confirmRemove"))) return;
    await api.delete(`/team/members/${id}`);
    load();
  };

  return (
    <div className="tb-page">
      <h1 className="tb-h1" data-testid="team-heading">{isAccelerator ? t("team.acceleratorTitle") : t("team.title")}</h1>
      <p className="tb-muted" style={{ marginBottom: "1.5rem", maxWidth: 760 }}>{isAccelerator ? t("team.acceleratorSubtitle") : t("team.subtitle")}</p>

      {err && <div className="tb-form-error" style={{ marginBottom: 12 }}>{err}</div>}
      {ok && <div className="tb-badge good" style={{ marginBottom: 12 }} data-testid="team-ok">✓ {ok}</div>}

      <div className="tb-block" style={{ marginBottom: "1.5rem" }}>
        <h3 style={{ marginTop: 0 }}>{t("team.members")}</h3>
        {isAccelerator && (
          <p className="tb-muted" style={{ marginTop: 0, marginBottom: 12, fontSize: "0.85rem", maxWidth: 720 }}>{t("team.acceleratorIntro")}</p>
        )}
        <div className="tb-card-grid">
          {data.owner && (
            <div className="tb-block" data-testid="team-owner">
              <strong>{data.owner.name}</strong>
              <span className="tb-badge good" style={{ marginLeft: 8 }}>{t("team.primary")}</span>
              <div className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 6 }}>{data.owner.email}</div>
            </div>
          )}
          {data.members.map((m) => (
            <div className="tb-block" key={m.id} data-testid={`team-member-${m.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div>
                  <strong>{m.name}</strong>
                  <div className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 4 }}>{m.email}</div>
                </div>
                {data.can_invite && (
                  <button className="tb-funnel-del" onClick={() => removeMember(m.id)} aria-label="remove" data-testid={`remove-member-${m.id}`}>×</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.invites.length > 0 && (
        <div className="tb-block" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ marginTop: 0 }}>{t("team.pendingInvites")}</h3>
          <div className="tb-card-grid">
            {data.invites.map((i) => (
              <div className="tb-block" key={i.id} data-testid={`invite-${i.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div>
                    <div className="tb-badge good" data-testid={`invite-sent-${i.id}`} style={{ marginBottom: 8 }}>
                      ✓ {t("team.inviteSent")}
                    </div>
                    <strong>{i.email}</strong>
                    <div className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 4 }} data-testid={`invite-sent-at-${i.id}`}>
                      {t("team.lastSent", { when: new Date(i.last_sent_at || i.created_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "medium", timeStyle: "short" }) })}
                    </div>
                    <div className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 4 }}>
                      {t("team.expires")} {new Date(i.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                  {data.can_invite && (
                    <button className="tb-funnel-del" onClick={() => cancelInvite(i.id)} aria-label="cancel" data-testid={`cancel-invite-${i.id}`}>×</button>
                  )}
                </div>
                {data.can_invite && (
                  <button
                    type="button"
                    className="tb-ghost-btn"
                    onClick={() => resendInvite(i.id)}
                    disabled={busy}
                    data-testid={`resend-invite-${i.id}`}
                    style={{ marginTop: 12 }}
                  >
                    {t("team.resend")}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.can_invite && !adding && (
        <button className="tb-primary-btn" onClick={() => setAdding(true)} data-testid="team-add">
          + {t("team.invite")}
        </button>
      )}
      {data.can_invite && adding && (
        <form onSubmit={submit} className="tb-block" style={{ marginTop: 16 }}>
          <div>
            <label>{t("auth.email")}</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cofounder@example.com" data-testid="invite-email" />
          </div>
          <div style={{ marginTop: 16 }}>
            <label>{t("auth.name")} <span className="tb-muted" style={{ fontWeight: 400 }}>({t("team.optional")})</span></label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button type="submit" className="tb-primary-btn" disabled={busy} data-testid="invite-submit">
              {busy ? t("auth.loading") : t("team.sendInvite")}
            </button>
            <button type="button" className="tb-ghost-btn" onClick={() => { setAdding(false); setForm({ email: "", name: "" }); }}>
              {t("book.cancel")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
